// İSG uzmanı portalı — Eğitim videosu yükleme alanı (§8 presigned; büyük dosya).
//
// Video **büyük dosyadır** (§16): yükleme §8 akışıyla yapılır (`documentsApi.uploadDocument`
// = create → doğrudan S3 PUT (XHR ilerleme) → confirm). Belge yükleme deseni (ContractorDocument
// DocumentLinkModal) ile **aynı** akış; yalnız boyut/tip ön-doğrulaması video içindir
// (`validateVideoFile` — daha yüksek boyut eşiği). Yeni upload component icat edilmez.
//
// Yüklenince `videoDocumentUuid` (+ dosya adı) yukarı bildirilir; forma bu değer bağlanır
// (eğitim create/update gövdesinde link'lenir). "Kaldır" video bağını temizler; yeni yükleme
// mevcut referansı değiştirir. İçerik sunucudan geçmez (doğrudan S3).

import {
  Alert,
  Button,
  VIDEO_ACCEPT_ATTR,
  documentsApi,
  resolveApiErrorMessage,
  validateVideoFile,
} from '@isgportal/shared';
import { useMutation } from '@tanstack/react-query';
import { FileVideo, Trash2, Upload } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface VideoUploadFieldProps {
  /** Halihazırda link'li §8 video dokümanı (yoksa null). */
  videoDocumentUuid: string | null;
  /** Görüntülenecek dosya adı (yoksa null). */
  videoFileName: string | null;
  /** Video referansı değiştiğinde (yükleme sonrası dolu; "Kaldır" ile null). */
  onChange: (documentUuid: string | null, fileName: string | null) => void;
  /** Form gönderimi sırasında girişi kilitler. */
  disabled?: boolean;
}

export function VideoUploadField({
  videoDocumentUuid,
  videoFileName,
  onChange,
  disabled,
}: VideoUploadFieldProps) {
  const { t } = useTranslation();
  const [fileError, setFileError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);

  const mutation = useMutation({
    mutationFn: async (file: File) => {
      setProgress(0);
      // §8 — doğrudan S3'e yükle + confirm → confirmed DocumentDto.
      return documentsApi.uploadDocument(file, {
        onProgress: (percent) => setProgress(percent),
      });
    },
    onSuccess: (document) => {
      onChange(document.uuid, document.fileName);
    },
    onSettled: () => setProgress(null),
  });

  function pickFile(files: File[]) {
    const picked = files[0];
    if (!picked) {
      return;
    }
    const invalid = validateVideoFile(picked);
    if (invalid) {
      setFileError(t(invalid));
      return;
    }
    setFileError(null);
    mutation.mutate(picked);
  }

  const uploading = mutation.isPending;
  const hasVideo = videoDocumentUuid !== null;

  return (
    <div className="flex flex-col gap-3">
      {mutation.isError ? (
        <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
      ) : null}
      {fileError ? <Alert tone="danger">{fileError}</Alert> : null}

      {hasVideo ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-surface px-3 py-3">
          <FileVideo className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate text-sm text-fg">
            {videoFileName ?? t('trainings.editor.video.linked')}
          </span>
          <div className="flex items-center gap-2">
            <label
              className={`inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border border-border-strong bg-surface px-3 text-sm font-medium text-fg hover:bg-surface-muted ${
                disabled || uploading ? 'pointer-events-none opacity-60' : ''
              }`}
            >
              <Upload className="h-4 w-4" aria-hidden="true" />
              {t('trainings.editor.video.replace')}
              <input
                type="file"
                className="sr-only"
                accept={VIDEO_ACCEPT_ATTR}
                disabled={disabled || uploading}
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []); // önce snapshot
                  e.target.value = ''; // sonra reset (aynı dosya tekrar seçilebilsin)
                  if (files.length) {
                    pickFile(files);
                  }
                }}
              />
            </label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled || uploading}
              onClick={() => onChange(null, null)}
            >
              <Trash2 className="h-4 w-4 text-danger" aria-hidden="true" />
              {t('trainings.editor.video.remove')}
            </Button>
          </div>
        </div>
      ) : (
        <label
          className={`flex cursor-pointer flex-col items-center gap-2 rounded-md border border-dashed border-border-strong bg-surface px-3 py-6 text-center text-sm text-muted hover:bg-surface-muted ${
            disabled || uploading ? 'pointer-events-none opacity-60' : ''
          }`}
        >
          <FileVideo className="h-6 w-6" aria-hidden="true" />
          <span>{t('trainings.editor.video.placeholder')}</span>
          <span className="text-xs">{t('trainings.editor.video.hint')}</span>
          <input
            type="file"
            className="sr-only"
            accept={VIDEO_ACCEPT_ATTR}
            disabled={disabled || uploading}
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []); // önce snapshot
              e.target.value = ''; // sonra reset
              if (files.length) {
                pickFile(files);
              }
            }}
          />
        </label>
      )}

      {progress !== null ? (
        <div className="flex flex-col gap-1" aria-live="polite">
          <progress
            className="sr-only"
            value={progress}
            max={100}
            aria-label={t('trainings.editor.video.uploading')}
          />
          <div
            className="h-2 w-full overflow-hidden rounded-full bg-surface-muted"
            aria-hidden="true"
          >
            <div
              className="h-full bg-primary transition-[width] duration-150"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-xs text-muted">
            {t('trainings.editor.video.progress', { percent: progress })}
          </span>
        </div>
      ) : null}
    </div>
  );
}
